/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable react-hooks/exhaustive-deps */
'use client';

import { Success as IconSuccess } from '@ahamove/icons';
import {
  estimateNewOrderFn,
  type EstimateNewOrderResponse,
  type OptimizeRoutesTspRequestBody,
  type Path,
  type PathLatLng,
} from '@ahm/api-wrappers-core';
import { useLocationStore } from '@ahm/react-map/store';
import { toast } from '@ahm/react-ui';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useQueryState, parseAsArrayOf, parseAsString } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useOptimizeRoutesTsp } from '@/features/booking/api/route-optimization';
import { useBookingRequestBodies } from '@/features/booking/hooks/use-booking-request';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type useOptimizedRouteHook = {
  tspOptimizeBody: OptimizeRoutesTspRequestBody;
  mutateOptimizeRoutesTsp: () => void;
  handleOptimizeRoutes: () => void;
  checkNeedOptimize: boolean;
  isLoadingOptimized: boolean;
  estimatedShortestRoute?: EstimateNewOrderResponse;
};

export const useOptimizedRouteHook = () => {
  const tBooking = useTranslations('booking');
  const pathname = usePathname();
  const [paramPromo] = useQueryState(
    'promo_code',
    parseAsArrayOf(parseAsString).withDefault([])
  );
  const { estimateRequestBody } = useBookingRequestBodies(pathname, paramPromo);

  const paths = useLocationStore.use.paths();
  const setPaths = useLocationStore.use.setPaths();
  const shortestRouteIndex = useLocationStore.use.shortestRouteIndex();
  const setShortestRouteIndex = useLocationStore.use.setShortestRouteIndex();

  const { estimatedData } = useBookingStore((state) => state);

  const tspOptimizeBody = useMemo(() => {
    if (paths && paths?.length > 2) {
      const depot: PathLatLng = [paths[0].lat!, paths[0].lng!];
      const points: PathLatLng[] = [];
      paths.forEach((item, i) => {
        if (i > 0) {
          points.push([item.lat, item.lng]);
        }
      });
      return {
        algorithms: 'tsp',
        depot,
        transport_mode: '1N',
        points,
        min_vehicles: true,
        max_stop_point: paths.length - 1,
        vehicle_num: 1,
        max_distance: 100000,
        esg_summary: false,
        // dimensions: [],
      };
    }
  }, [paths]);

  const checkNeedOptimize = useMemo(() => {
    if (shortestRouteIndex?.length) {
      return shortestRouteIndex.some((item, index) => {
        return item !== index;
      });
    }
    return false;
  }, [shortestRouteIndex]);

  const estimatedShortestRoute = useCallback(async () => {
    try {
      if (
        !paths ||
        paths.length <= 2 ||
        !shortestRouteIndex ||
        !estimatedData?.data ||
        !estimateRequestBody
      )
        return undefined;

      const newPaths: Path[] = [paths[0]];
      const dropOffs = paths.slice(1);
      shortestRouteIndex.forEach((item) => {
        newPaths.push(dropOffs[item]);
      });

      const optimizedEstFee = await estimateNewOrderFn({
        ...estimateRequestBody,
        path: newPaths,
      });
      if (!optimizedEstFee[0] || !estimatedData) return undefined;
      const optimizedEstFeeData: EstimateNewOrderResponse = optimizedEstFee[0];

      if (optimizedEstFeeData?.data) {
        return optimizedEstFeeData;
      }
    } catch (error) {
      console.log('error', error);
      return null;
    }
  }, [paths, shortestRouteIndex, estimatedData, estimateRequestBody]);

  const handleOptimizeRoutes = useCallback(() => {
    if (!checkNeedOptimize) return;
    const newPaths: Path[] = [paths[0]];
    const dropOffs = paths.slice(1);

    shortestRouteIndex.forEach((item) => {
      newPaths.push(dropOffs[item]);
    });

    setPaths(newPaths);
    toast.success({
      title: tBooking('location.optimized_the_best_route_for_you'),
      withIcon: true,
      withBorder: false,
      icon: <IconSuccess />,
      style: { backgroundColor: '#E8F9EF' },
      withCloseButton: false,
      message: null,
      styles: {
        root: {
          alignItems: 'center',
        },
        icon: {
          background: 'none',
        },
        title: {
          fontSize: 16,
        },
      },
    });
  }, [checkNeedOptimize, paths, setPaths, shortestRouteIndex]);

  const {
    mutateAsync: mutateOptimizeRoutesTsp,
    isPending: isLoadingOptimized,
  } = useOptimizeRoutesTsp({
    onSuccess(data) {
      if (data.code === 200 && data.result.cluster.length >= 2) {
        setShortestRouteIndex(data.result.cluster);
      }
    },
    onError(error) {
      console.log('error', error);
    },
  });

  return {
    handleOptimizeRoutes,
    checkNeedOptimize,
    tspOptimizeBody,
    isLoadingOptimized,
    mutateOptimizeRoutesTsp,
    estimatedShortestRoute,
  };
};
